import { create } from 'zustand';

import {
  apiFetchNotifications,
  apiMarkNotificationsAsDismissed,
  apiMarkNotificationsAsRead,
  apiMarkNotificationsAsViewed,
} from 'api/notification/apiUserNotificationEndpoints';
import { ApiNotification } from 'api/notification/types/ApiNotification';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import {
  getMappedNotificationIds,
  sortNotifications,
} from 'utils/userNotifications';

const userNotificationsStore = create<{
  notifications: Array<MappedNotification | ApiNotification>;
  isFetching?: boolean;
  isLoaded: boolean;
}>(() => ({
  notifications: [],
  isFetching: false,
  isLoaded: false,
}));

const useUserNotificationsStore = userNotificationsStore; // The store can be used as a hook

function setNotifications(notifications: ApiNotification[]) {
  userNotificationsStore.setState({
    notifications: sortNotifications(notifications),
    isFetching: false,
    isLoaded: true,
  });
}

// Actions

export function fetchNotifications() {
  userNotificationsStore.setState({ isFetching: true });
  return apiFetchNotifications().then(setNotifications);
}

export function markNotificationsAsViewed(
  notifications: Array<MappedNotification | ApiNotification>,
) {
  const ids = notifications.flatMap((notification) =>
    getMappedNotificationIds(notification),
  );
  return apiMarkNotificationsAsViewed(ids).then(setNotifications);
}

export function markNotificationAsRead(
  notification: MappedNotification | ApiNotification,
) {
  if (
    notification.type === 'NEW_APPLICATIONS_BY_LISTING' ||
    notification.type === 'NEW_SUGGESTED_APPLICANTS_BY_JOB' ||
    notification.type === 'NEW_RSVP_BY_LISTING'
  ) {
    const readIds = notification.data.notifications
      .filter((n) => n.read !== null)
      .map((n) => n.id);
    const unreadIds = notification.data.notifications
      .filter((n) => n.read === null)
      .map((n) => n.id);
    return apiMarkNotificationsAsDismissed(readIds).then(() =>
      apiMarkNotificationsAsRead(unreadIds).then(setNotifications),
    );
  }
  return apiMarkNotificationsAsRead([notification.id]).then(setNotifications);
}

export function markNotificationAsDismissed(
  notification: MappedNotification | ApiNotification,
) {
  const ids = getMappedNotificationIds(notification);
  return apiMarkNotificationsAsDismissed(ids).then(setNotifications);
}

// Hook

export function useUserNotifications() {
  const notifications = useUserNotificationsStore(
    (state) => state.notifications,
  );

  const isFetching = useUserNotificationsStore((state) => state.isFetching);

  return { notifications, isFetching };
}
